/* eslint-disable max-lines */

import { enPaymentMethod } from 'enums/enPaymentMethod';
import { enPaymentType } from 'enums/enPaymentType';
import { enCurrency, IProductData, IShipping, IShippingData, ICouponData, IProduct } from 'interfaces/IProductData';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { paymentService } from 'services/PaymentService';
import { CACHED_MOUNT, CART_SSID, IGNORE_CACHE, TRACKER_DATA, IS_ECOMMERCE, IS_SHOPIFY, SHOPIFY_SHOP, GOOFY_ID } from 'settings';
import { round2 } from 'shared/functions';
import { Cookies } from 'react-cookie';

import { IProductTemplateConfig } from '../interfaces/IProductData';
import { CACHE_ID } from '../settings';
import { cacheService } from './CacheService';
import { warmapCache } from './InstallmentsService';
import { parameterService } from './ParameterService';
import { apiService } from 'shared/services';
import { ICartModel, IProductModel } from 'interfaces/ICartModel';
import { socketService } from './SocketService';
import { legacyPixelService } from 'services/LegacyPixelService';
import { translateService } from './TranslateService';
import { enProductType } from 'enums/enProductType';
import { enParameters } from 'enums/enParameters';
import { ticketService } from 'services/TicketService';
import { of } from 'rxjs/observable/of';
import { formatScripts } from 'functions';
import { scriptsService } from './ScriptsService';
import { showAlert, showError } from 'shared/services/dialog';
import { IOrderBumpResult } from 'interfaces/IOrderBumpResult';
import { shopifyService } from './ShopifyService';
import { currencyService } from './CurrencyService';
import goofyService from './GoofyService';
import { configService } from './ConfigService';

export interface ICartConfigReplacer {
  templateId?: number;
  template?: IProductTemplateConfig;
}

const cookie = new Cookies();

export class CartService {
  private cart$ = new ReplaySubject<IProductData>(1);
  private uniqueCart$ = this.cart$
    .asObservable()
    .map(state => {
      (state.isCached as any) = !this.mounted;
      return state;
    })
    .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));

  private loaded = false;
  private mounted = false;

  constructor() {
    this.trackItemsGoofy();
  }

  public getCart(): Observable<IProductData> {
    return this.uniqueCart$;
  }

  public getMappedCart<T>(fn: (cart: IProductData) => T) {
    return this.getInternalCart()
      .map(fn)
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  public getConfig() {
    return configService.getConfig();
  }

  public setShippingData(products: IProductModel[], postalCode: string = null): Observable<IShippingData> {
    if (products.length === 0) {
      return Observable.empty();
    }

    return Observable.combineLatest(this.getInternalCart(), paymentService.hasPaymentSent())
      .take(1)
      .filter(([cart, paymentSent]) => {
        return !cart.isCached && !paymentSent;
      })
      .switchMap(() => paymentService.getCart())
      .switchMap(cart => {
        return Observable.combineLatest(Observable.of(cart), shopifyService.getShippingData(cart.address));
      })
      .take(1)
      .switchMap(([payment, shopifyShipping]) => {
        const body = {
          transactionId: payment.ssid,
          postalCode,
          products,
          shopifyShipping
        };

        if (body.postalCode == 'undefined') {
          body.postalCode = null;
        }

        return apiService.post<IShippingData>('/shipping/estimate', body).loader();
      })
      .combineLatest(this.getInternalCart().take(1))
      .map(([data, cart]) => {
        warmapCache({
          installments: data.installments,
          price: data.price
        });

        data.shippingItems.forEach(i => {
          warmapCache({
            installments: i.installments,
            price: i.totalValue
          });
        });

        paymentService.setBlockPayment(false);

        const newCart: IProductData = JSON.parse(JSON.stringify(cart));
        (newCart.installments as any) = data.installments;
        (newCart.price as any) = data.price;
        (newCart.realPrice as any) = data.realPrice;
        (newCart.products as any) = data.products;
        (newCart.tax as any) = data.tax;
        (newCart.realTax as any) = data.realTax;
        (newCart.taxedPrice as any) = data.taxedPrice;
        (newCart.cartPrice as any) = data.cartPrice;

        if (newCart.config.productType == enProductType.TICKET) {
          newCart.tickets.reservationData = data.reservation;

          const hasError = cart.tickets.reservationData.products.some(p => p.success == false);

          if (hasError) {
            // tslint:disable-next-line:max-line-length
            showAlert(
              'Infelizmente a quantidade de tickets selecionada é maior que o nosso estoque atual. Tente novamente mais tarde.',
              'Ops!'
            ).subscribe();
          }
        }

        this.cart$.next(newCart);

        if (newCart.tickets) {
          ticketService.setTimeLeft(newCart.tickets.reservationData.expiresIn);
        }

        paymentService.setData({
          quantity: data.products[0].quantity
        });

        paymentService.setPaymentData(0, {
          value: data.price,
          installments: data.installments,
          numberInstallments: data.installments.length
        });

        return data;

        this.setShipping(data.hash, data.shippingItems[0]);

        return data;
      })
      .catch(err => {
        paymentService.setBlockPayment(true);

        showError(err).subscribe();
        return Observable.empty();
      });
  }

  public setShipping(hash: string, selectedShipping: IShipping): void {
    this.getInternalCart()
      .take(1)
      .map(cart => {
        const newCart: IProductData = JSON.parse(JSON.stringify(cart));

        (newCart.price as any) = selectedShipping.totalValue;
        (newCart.taxedPrice as any) = selectedShipping.taxedPrice;
        (newCart.tax as any) = selectedShipping.tax;
        (newCart.cartPrice as any) = selectedShipping.cartPrice;
        (newCart.realPrice as any) = selectedShipping.realPrice;
        (newCart.installments as any) = selectedShipping.installments;

        this.cart$.next(newCart);
        paymentService.setData({
          shipping: {
            code: selectedShipping.code,
            hash
          }
        });
        paymentService.setPaymentData(0, {
          value: selectedShipping.totalValue,
          installments: selectedShipping.installments,
          numberInstallments: selectedShipping.installments.length
        });

        return true;
      })
      .subscribe();
  }

  public setOrderBumps(products: string[]): Observable<any> {
    return this.getInternalCart()
      .take(1)
      .switchMap(cart => {
        return Observable.combineLatest(
          Observable.of(products),
          Observable.of(cart),
          apiService.post<IOrderBumpResult>(`/bump/${cart.transactionKey}/${cart.contentId}`, { products })
        );
      })
      .map(([bump, cart, bumpData]) => {
        goofyService.track({
          flow: 'sale',
          trackerId: GOOFY_ID,
          step: 'bump_selected',
          data: {
            hasOrderBump: bump.length > 0
          }
        });

        const newCart = JSON.parse(JSON.stringify(cart));

        const mergedCart = { ...newCart, ...bumpData };
        mergedCart.installments = bumpData.installments;

        this.cart$.next(mergedCart);
        paymentService.setPaymentData(0, {
          value: bumpData.price,
          installments: bumpData.installments
        });

        const products: IProductModel[] = [];

        mergedCart.products.map((product: IProduct) => {
          products.push({
            contentId: product.id.toString(),
            quantity: product.quantity
          });
        });

        paymentService.setData({ products });

        return mergedCart;
      });
  }

  public setCoupon(coupon: string, couponType: number = 1): Observable<boolean> {
    paymentService.addLock();

    return this.getInternalCart()
      .filter(cart => !!cart.transactionKey)
      .take(1)
      .switchMap(cart => {
        const url =
          couponType === 1
            ? `/coupon/${cart.transactionKey}/product/${cart.contentId}`
            : `/coupon/${cart.transactionKey}/product/${cart.contentId}/modal`;

        return apiService.post<ICouponData>(url, {
          coupon
        });
      })
      .switchMap(coupon => this.updateCouponData(coupon))
      .finally(() => {
        paymentService.removeLock();
      });
  }

  public enableAskAddress(): Observable<IProductData> {
    return this.getInternalCart()
      .take(1)
      .filter(cart => cart.config.askAddressAfterPayment || cart.config.askAddressBeforePayment)
      .do(cart => {
        const newCart: IProductData = JSON.parse(JSON.stringify(cart));

        (newCart.config.askAddressAfterPayment as any) = false;
        (newCart.config.askAddressBeforePayment as any) = true;

        this.cart$.next(newCart);
      });
  }

  public removeCoupon(): Observable<boolean> {
    return this.getInternalCart()
      .take(1)
      .switchMap(cart => {
        return apiService.del<ICouponData>(`/coupon/${cart.transactionKey}/product/${cart.contentId}`);
      })
      .switchMap(coupon => this.updateCouponData(coupon));
  }

  public updateCouponData(coupon: ICouponData): Observable<boolean> {
    return Observable.of(coupon)
      .combineLatest(this.getInternalCart().take(1), paymentService.getCart().take(1))
      .map(([coupon, cart, payment]) => {
        const newCart = this.createNewCart(cart, coupon);

        // REMOVER APÓS CORRIGIDO NA API
        if (newCart.config.paymentType === enPaymentType.SUBSCRIPTION) {
          (newCart.subscription.tax as any) = coupon.price;
        }

        warmapCache(newCart);

        this.cart$.next(newCart);

        const newValue = round2(newCart.price / payment.payment.length);

        let lastPaymentValue = newValue + (newCart.price - newValue * payment.payment.length);

        lastPaymentValue = round2(lastPaymentValue);

        payment.payment.forEach((p, key) => {
          const selectedInstallment = p.numberInstallments;

          const maxInstallments =
            newCart.config.maxInstallments < newCart.installments.length ? newCart.config.maxInstallments : newCart.installments.length;

          const numberInstallments = maxInstallments >= selectedInstallment ? selectedInstallment : maxInstallments;

          paymentService.setPaymentData(key, {
            value: key != payment.payment.length - 1 ? newValue : lastPaymentValue,
            installments: newCart.installments,
            numberInstallments
          });
        });

        return true;
      })
      .logError();
  }

  public disableOneClickBuy(): Observable<void> {
    cookie.remove('he');
    cookie.remove('email');

    return this.getInternalCart()
      .take(1)
      .map(cart => {
        const newCart: IProductData = JSON.parse(JSON.stringify(cart));
        (newCart.config.isOneClick as any) = false;
        this.cart$.next(newCart);

        return null;
      });
  }

  public getCurrentState(): Observable<IProductData> {
    return this.getInternalCart().first();
  }

  public loadContent(contentId: string, currency: enCurrency): Observable<boolean> {
    let transactionKey = CART_SSID;

    const cachedMount = CACHED_MOUNT;

    let isEcommerce = false;
    let isShopify = false;
    let isRec = false;

    let loadObservable = parameterService
      .getAll()
      .take(1)
      .switchMap(parameters => {
        if (cachedMount) {
          return of(parameters).delay(3000);
        }
        return of(parameters);
      })
      .combineLatest(shopifyService.getShopifyCartData())
      .switchMap(([parameters, shopifyData]) => {
        const cookie = new Cookies();
        if (IS_ECOMMERCE || !!`${contentId}`.match(/^c_/)) {
          contentId = contentId.replace(/^c_/, '');
          parameters = { ...parameters, ec: '1' };
          isEcommerce = true;
        }

        if (IS_SHOPIFY) {
          contentId = cookie.get('cart');
          parameters = {
            ...parameters,
            shopify: {
              lang: cookie.get('lang'),
              currency: cookie.get('cart_currency'),
              shop: SHOPIFY_SHOP,
              products: shopifyData
            }
          };
          isShopify = true;
        }

        if (!!`${contentId}`.match(/^r_/)) {
          contentId = contentId.replace(/^r_/, '');
          parameters = { ...parameters, rec: '1' };
          isRec = true;
        }

        const lastPurchase = cookie.get('last_purchase');
        if (parameters.u && lastPurchase) {
          delete parameters.u;
          parameters.upsell = true;
        }

        return apiService.put<IProductData>(`/cart/${transactionKey}`, {
          contentId,
          currency,
          parameters,
          tracker: TRACKER_DATA,
          goofyId: GOOFY_ID
        });
      })
      .catch(err => {
        if (err.code === 'TRA_A') {
          cookie.remove(`goofy-${contentId}`, {
            path: '/',
            domain: 'eduzz.com'
          });
          window.location.reload();
          return Observable.empty();
        }

        return Observable.throw(err);
      })
      .map((data: IProductData) => {
        (data.config.scripts as any) = formatScripts(data.config.scripts);
        (data.isCached as any) = false;

        const products: IProductModel[] = [];

        data.products.map(p =>
          products.push({
            contentId: p.id.toString(),
            quantity: p.quantity || 1
          })
        );

        paymentService.setData({ products });

        scriptsService.setScripts(data.config.scripts);

        if (window.TEMPLATE_CONFIG) {
          const templateConfig: any = window.TEMPLATE_CONFIG;

          (data.config.template as any) = {
            id: templateConfig.template,
            inputStyle: 'normal',
            styles: templateConfig,
            media: {
              bannerCenter: templateConfig.bannerCenter,
              bannerTop: templateConfig.bannerTop,
              bannerRight: templateConfig.bannerRight,
              bannerYoutube: templateConfig.bannerYoutube
            }
          };
        }

        return data;
      });

    if (!this.loaded) {
      this.loaded = true;
      loadObservable = loadObservable.startWith(cachedMount);
    }

    const observable = loadObservable
      .filter(data => !!data)
      .combineLatest(paymentService.getCart().take(1))
      .map(([data, paymentData]) => {
        if (!parameterService.getSync(enParameters.pu)) {
          parameterService.setParameter(enParameters.pu, `${data.config.maxInstallments}`);
        }

        currency = data.config.currency;

        data.originalContentId = contentId;

        let installments = data.installments;

        if (data.upsell) {
          const oneX = installments.find(x => x.installment === 1);
          installments = [oneX];
        }

        (data.installments as any) = installments;

        socketService.emit('cart', {
          contentId: data.contentId,
          producerId: data.config.producerId,
          transactionKey
        });

        warmapCache(data);

        if (!IGNORE_CACHE && !data.isCached && !data.user && !data.upsell) {
          cacheService.setCache(CACHE_ID, data);
        }

        if (!data.isCached) {
          this.mounted = true;
        }

        /*
          legacyPixelService: Should be removed in not too distance future
        */
        legacyPixelService.setPixel({ ...data.config.pixel, contentId });

        currencyService.setCurrency(currency);

        if (!paymentData.contentId) {
          this.initializePayment(data.contentId, transactionKey, data, paymentData);
        }

        paymentService.loadParameters();

        if (currency != enCurrency.BRL) {
          paymentService.setPaymentData(0, {
            numberInstallments: 1,
            installments: data.installments
          });
        }

        if (data.upsell) {
          paymentService.setPaymentData(0, {
            paymentMethod: data.upsell,
            numberInstallments: 1
          });
          this.initializePayment(isEcommerce || isRec || isShopify ? data.contentId : data.originalContentId, transactionKey, data, paymentData);
        }

        if (!data.config.enableCreditcard) {
          let pm = null;

          if (data.config.enablePaypal) {
            pm = enPaymentMethod.PAYPAL;
          }

          if (data.config.enablePaypal && currency !== enCurrency.BRL) {
            pm = enPaymentMethod.PAYPAL;
          }

          if (data.config.enableBankslip && currency !== enCurrency.USD) {
            pm = enPaymentMethod.BOLETO;
          }

          if (data.config.paymentType === enPaymentType.FREE) {
            pm = enPaymentMethod.FREE;
          }

          if (pm) {
            paymentService.setPaymentData(0, {
              paymentMethod: pm
            });
          }

          if (!pm) {
            (data.config.enableCreditcard as any) = true;
          }
        }

        if (data.tickets) {
          ticketService.setTimeLeft(data.tickets.reservationData.expiresIn);
          ticketService.setReserveTime(data.tickets.reservationData.reserveTime);
        }

        configService.setConfig(data.config);

        this.cart$.next(data);

        if (data.next) {
          if (!data.next.plans) {
            paymentService.setData({ next: { withoutLimitation: true } });
          }
        }

        paymentService.setPaymentData(0, { installments });

        if (data.upsell) {
          console.log('setted to: ', data.upsell);
          paymentService.setPaymentData(0, {
            paymentMethod: data.upsell == 'boleto' ? enPaymentMethod.BOLETO : data.upsell,
            numberInstallments: 1
          });
        }

        if (data.installmentSelected > 0) {
          paymentService.setDefaultInstallment(data.installmentSelected);
        }

        return true;
      })
      .logError();

    return translateService.loadTranslations().switchMap(() => observable);
  }

  public setCart = (cart: IProductData, propriety: any) => {
    const newCart = {
      ...cart,
      ...propriety
    };

    this.cart$.next(newCart);
  };

  protected getInternalCart(): Observable<IProductData> {
    return this.uniqueCart$;
  }

  private createNewCart(cart: IProductData, coupon: ICouponData) {
    let newCart: IProductData = JSON.parse(JSON.stringify(cart));

    let cartCoupon = null;

    if (coupon.coupon) {
      cartCoupon = {
        code: coupon.coupon.code,
        value: coupon.coupon.value
      };
    }

    newCart = {
      ...newCart,
      price: coupon.price,
      realPrice: coupon.realPrice,
      tax: coupon.tax,
      cartPrice: coupon.cartPrice,
      realTax: cart.realTax,
      taxedPrice: coupon.taxedPrice,
      coupon: cartCoupon,
      numberInstallments: coupon.installments.length,
      installments: coupon.installments,
      products: coupon.products,
      config: {
        ...newCart.config,
        maxInstallments: cart.config.maxInstallments || coupon.installments.length
      }
    };

    return newCart;
  }

  private initializePayment(contentId: string, ssid: string, data: IProductData, paymentData: ICartModel) {
    paymentService.setData({
      contentId,
      ssid,
      fullPrice: Number(data.price.toFixed(2)),
      quantity: data.products[0].quantity,
      productType: data.config.productType
    });

    paymentService.setPaymentData(0, {
      paymentMethod:
        data.config.paymentType == enPaymentType.FREE ? enPaymentMethod.FREE : data.config.isOneClick ? enPaymentMethod.ONE_CLICK_BUY : null,
      installments: data.installments,
      numberInstallments: data.numberInstallments,
      value: data.price
    });

    if (data.user) {
      const user = data.user || {};
      paymentService.setData({
        name: paymentData.name || user.name,
        email: paymentData.email || user.email,
        phone: paymentData.phone || user.phone,
        document: paymentData.document || user.document
      });

      const currentAddress = paymentData.address || {};

      if (user.postalCode) {
        paymentService.setAddressData({
          postalCode: currentAddress.postalCode || user.postalCode,
          street: currentAddress.street || user.street,
          number: currentAddress.number || user.number,
          district: currentAddress.district || user.district,
          complement: currentAddress.complement || user.complement,
          province: currentAddress.province || user.province,
          cityId: currentAddress.cityId || user.cityId.toString(),
          city: currentAddress.city || user.city
        });
      }

      if (user.creditCard) {
        paymentService.setPaymentData(0, {
          creditCardId: user.creditCard.id,
          brand: user.creditCard.brand
        });
      }
    }
  }

  private trackItemsGoofy() {
    this.getCart()
      .filter(cart => !!cart && !!cart.products)
      .map(cart => cart.products)
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
      .debounceTime(500)
      .subscribe(products => {
        goofyService.track({
          flow: 'sale',
          trackerId: GOOFY_ID,
          step: 'cart_items',
          data: {
            items: products
          }
        });
      });
  }
}

// tslint:disable-next-line:variable-name
export const CartServiceFactory = () => new CartService();

export const cartService = CartServiceFactory();
